/* import __COLOCATED_TEMPLATE__ from './procedure-list-item-stats.hbs'; */
/* RESPONSIBLE TEAM: team-actions */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';

interface ProcedureListItemStatsSignature {
  Args: {
    procedureId: string;
  };
  Element: HTMLDivElement;
}

// All stats use the same count metric, filtered by resolution state
const METRIC_ID = 'fin.conversations_participated.count';

type StatType = 'triggered' | 'resolved' | 'handoff' | 'escalated' | 'pending' | 'failed';

export default class TasksProcedureListItemStats extends Component<ProcedureListItemStatsSignature> {
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked selectedProcedureId?: string;
  @tracked selectedStat?: StatType;

  drillInColumns = [
    'conversation.id',
    'conversation.first_user_conversation_part_created_at',
    'conversation.fin.resolution_state',
    'conversation.channel_type',
    'conversation.bot_conversation_rating.rating_index',
    'conversation.fin.procedures_triggered.procedure_ids',
  ];

  get canUseProcedureErrorReporting(): boolean {
    return this.appService.app.canUseFeature('procedures-error-reporting');
  }

  get chartDefinitions(): Record<string, any> {
    let definitions: Record<string, any> = {
      triggered: this.chartDefinitionForStat('triggered'),
      resolved: this.chartDefinitionForStat('resolved'),
      handoff: this.chartDefinitionForStat('handoff'),
      escalated: this.chartDefinitionForStat('escalated'),
      pending: this.chartDefinitionForStat('pending'),
    };

    if (this.canUseProcedureErrorReporting) {
      definitions.failed = this.chartDefinitionForStat('failed');
    }

    return definitions;
  }

  @action
  openSideDrawer(procedureId: string, stat: StatType, event: Event) {
    this.trackEvent(stat);
    event.stopPropagation();
    event.preventDefault();
    if (this.permissionsService.currentAdminCan('can_reporting__drillin__access')) {
      this.selectedProcedureId = procedureId;
      this.selectedStat = stat;
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  closeSideDrawer() {
    this.selectedProcedureId = undefined;
    this.selectedStat = undefined;
  }

  @action
  trackEvent(stat_clicked: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'procedure_stat',
      place: 'procedure_list_item',
      section: 'procedures',
      stat: stat_clicked,
    });
  }

  private chartDefinitionForStat(stat: string): any {
    let procedureId = this.args.procedureId;
    let procedureProperty =
      stat === 'failed'
        ? 'fin.procedures_failed.procedure_ids'
        : 'fin.procedures_triggered.procedure_ids';

    let filters: any[] = [
      {
        type: 'category',
        data: {
          property: procedureProperty,
          values: [procedureId],
        },
      },
    ];

    // Add resolution state filter based on stat type
    if (stat === 'resolved') {
      filters.push({
        type: 'category',
        data: {
          property: 'fin.resolution_state',
          values: ['confirmed_resolved', 'assumed_resolved'],
        },
      });
    } else if (stat === 'handoff') {
      filters.push({
        type: 'category',
        data: {
          property: 'fin.resolution_state',
          values: ['procedure_handoff'],
        },
      });
    } else if (stat === 'escalated') {
      filters.push({
        type: 'category',
        data: {
          property: 'fin.resolution_state',
          values: ['routed_to_team'],
        },
      });
    } else if (stat === 'pending') {
      filters.push({
        type: 'not_exists',
        data: {
          property: 'fin.resolution_state',
        },
      });
    }
    // 'triggered' and 'failed' stats don't need a resolution state filter

    return {
      id: `procedure_${procedureId}_${stat}`,
      visualization_type: 'counter',
      chart_series: [
        {
          metric_id: METRIC_ID,
          filters: {
            type: 'and',
            filters,
          },
        },
      ],
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Tasks::ProcedureListItemStats': typeof TasksProcedureListItemStats;
  }
}
